import { ChipColor } from './arnoAdminLabels';

/**
 * Darstellung des Ablaufs eines Entscheidungsbaums (#1245).
 *
 * Die Ansicht ist für Menschen, nicht für den Parser: eine Frage heißt „Frage",
 * eine Bedingung steht im Klartext, und Opcode, Register und Rohanweisung liegen
 * darunter. Welche Knotenart welche Beschriftung und welches Gewicht bekommt, ist
 * die eigentliche Aussage — deshalb hier als reine Funktionen.
 */

export type FlowKind =
  | 'section'
  | 'block'
  | 'text'
  | 'question'
  | 'choice'
  | 'condition'
  | 'input'
  | 'info'
  | 'stop'
  | 'legacy'
  | 'unresolved';

export interface IFlowNode {
  kind: FlowKind;
  title: string;
  detail?: string | null;
  note?: string | null;
  condition?: string | null;
  order?: number;
  blockId?: string | null;
  children?: IFlowNode[];
  technical?: Record<string, unknown> | null;
}

export interface IFlowView {
  idDecisionTemplate: number;
  templateId: string;
  name: string;
  group: string;
  variant: string;
  sourceType: string;
  lifecycleStatus: string;
  sections: IFlowNode[];
  blocksShown: number;
  maxDepth: number;
}

/** Was vor dem Knoten steht — die Art im Klartext, nicht der Opcode. */
export const FLOW_KIND_LABEL: Record<FlowKind, string> = {
  section: 'Abschnitt',
  block: 'Baustein',
  text: 'Text',
  question: 'Frage',
  choice: 'Auswahl',
  condition: 'Bedingung',
  input: 'Eingabe',
  info: 'Hinweis',
  stop: 'Ende',
  legacy: 'Technisch',
  unresolved: 'Offen',
};

/**
 * Farbe der Knotenart.
 *
 * Nur zwei Arten fallen auf: ein nicht auflösbarer Verweis, weil dort etwas fehlt,
 * und die Entscheidungsstellen (Frage, Auswahl, Bedingung), weil sie den Ablauf
 * verzweigen. Alles andere bleibt ruhig — sonst leuchtet die ganze Seite.
 */
export const flowKindColor = (kind: FlowKind): ChipColor => {
  switch (kind) {
    case 'unresolved':
      return 'warning';
    case 'question':
    case 'choice':
    case 'condition':
      return 'primary';
    case 'legacy':
      return 'default';
    default:
      return 'default';
  }
};

/** Entscheidungsstellen werden hervorgehoben, Fließtext nicht. */
export const isDecisionPoint = (kind: FlowKind): boolean =>
  kind === 'question' || kind === 'choice' || kind === 'condition';

/**
 * Technische Angaben eines Knotens als Beschriftungspaare.
 *
 * Leere Werte fallen weg, damit unter „Technische Details" nicht zehn Zeilen
 * „null" stehen. Die Reihenfolge ist stabil, damit der Blick sie wiederfindet.
 */
export const technicalEntries = (
  technical?: Record<string, unknown> | null
): { label: string; value: string }[] => {
  if (!technical) {
    return [];
  }
  return Object.entries(technical)
    .filter(
      ([, value]) =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
    )
    .map(([label, value]) => ({
      label,
      value: typeof value === 'object' ? JSON.stringify(value) : String(value),
    }));
};

/** Wie viele Knoten der Baum insgesamt zeigt — für die Zeile über der Ansicht. */
export const countNodes = (nodes: IFlowNode[]): number =>
  nodes.reduce((total, node) => total + 1 + countNodes(node.children ?? []), 0);

/** Wie viele Entscheidungsstellen darin vorkommen. */
export const countDecisionPoints = (nodes: IFlowNode[]): number =>
  nodes.reduce(
    (total, node) =>
      total +
      (isDecisionPoint(node.kind) ? 1 : 0) +
      countDecisionPoints(node.children ?? []),
    0
  );

/**
 * Die Zusammenfassung über dem Ablauf.
 *
 * Abschnitte und Entscheidungsstellen sagen, wie groß der Baum ist; die Tiefe
 * sagt, dass darunter noch mehr liegen kann. Ohne diesen Hinweis wirkt die
 * abgeschnittene Ansicht wie der ganze Baum.
 */
export const flowSummary = (view?: IFlowView): string => {
  if (!view) {
    return '';
  }
  const decisions = countDecisionPoints(view.sections);
  return `${view.sections.length} Abschnitte · ${view.blocksShown} Bausteine · ${decisions} Entscheidungsstellen · bis Ebene ${view.maxDepth}`;
};
